import { ChangeEvent, ClipboardEvent, KeyboardEvent, useCallback, useState } from 'react';

type UseEmailChipsInputParams = {
	emails: string[];
	onEmailsChange: ( emails: string[] ) => void;
};

const isValidEnterKey = (
	( event: KeyboardEvent ) => ( event.key === 'Enter' || event.key === ' ' || event.key === 'Tab' || event.key === ',' )
);

const useEmailChipsInput = ( { emails, onEmailsChange }: UseEmailChipsInputParams ) => {
	const [ inputValue, setInputValue ] = useState<string>( '' );

	const onKeyDown = useCallback( ( event: KeyboardEvent<HTMLInputElement> ) => {
		if ( !isValidEnterKey( event ) ) { return; }
		event.preventDefault();

		if ( !inputValue || emails.includes( inputValue ) ) { return; }

		onEmailsChange( [ ...emails, inputValue ] );
		setInputValue( '' );
	}, [ emails, inputValue, onEmailsChange ] );

	const onInputChange = useCallback( ( event: ChangeEvent<HTMLInputElement> ) => {
		setInputValue( event.currentTarget.value.trim() );
	}, [] );

	const onDeleteClick = useCallback( ( email: string ) => {
		onEmailsChange( emails.filter( e => e !== email ) );
	}, [ emails, onEmailsChange ] );

	const onPaste = useCallback( ( event: ClipboardEvent<HTMLInputElement> ) => {
		event.preventDefault();
		const emailsFromClipboard = event.clipboardData.getData( 'text/plain' )
			.split( /, +|,| +|\n/g )
			.map( email => email.trim() )
			.filter( email => email !== '' );

		const uniqueEmails = [ ...new Set( [ ...emails, ...emailsFromClipboard ] ) ];
		onEmailsChange( uniqueEmails );
	}, [ emails, onEmailsChange ] );

	const onBlur = useCallback( () => {
		if ( !inputValue ) { return; }

		if ( !emails.includes( inputValue ) ) {
			onEmailsChange( [ ...emails, inputValue ] );
		}

		setInputValue( '' );
	}, [ emails, inputValue, onEmailsChange ] );

	return {
		inputValue,
		onKeyDown,
		onInputChange,
		onDeleteClick,
		onPaste,
		onBlur
	};
};

export default useEmailChipsInput;
